<script setup>
import { ref, computed, watch, nextTick, onMounted, onBeforeUnmount } from 'vue'
import CaptionService from '@/services/CaptionService'
import FormButton from '@/components/Atoms/FormButton.vue'
import DropDownMenu from '@/components/Atoms/DropDownMenu.vue'
import DropDownMenuItem from '@/components/Atoms/DropDownMenuItem.vue'
import StatusPill from '@/components/Atoms/StatusPill.vue'
import IconBase from '@/components/icon/IconBase.vue'
import IconChevronDown from '@/components/icon/IconChevronDown.vue'
import { FALLBACK_CATALOG, languageName, joinLanguageNames, formatDuration } from './captionLanguages'
import {
	DEFAULT_FORMAT,
	NO_CAPTIONS,
	NOTHING_RECORDED,
	SNAPSHOT_NOTICE,
	sessionExportLangs,
	exportParts,
	isRunningSession,
	sessionLengthSec,
	formatSessionStart,
	filenameFromDisposition,
	fallbackFilename,
	exportErrorMessage,
	isExportPending,
	saveBlob,
} from './captionExport'
import { track, EVENTS } from '@/lib/tracking'

/**
 * The body of the "Download captions" modal: recent caption sessions with a
 * download menu per session (languages from the session, formats from
 * `GET /captions/:id/export`). Loads when mounted, i.e. when the modal opens.
 * Emits `unavailable` when core has no sessions/export endpoints (404, older
 * core) so the page can drop its entry points.
 */
const props = defineProps({
	flowId: { type: String, required: true },
	streamKey: { type: String, default: '' },
	catalog: { type: Object, default: () => FALLBACK_CATALOG },
	// Server clock minus client clock, so a running session's snapshot ends at server "now".
	skewMs: { type: Number, default: 0 },
	// Changes whenever the caption window starts/stops/ends: the list is re-read.
	refreshKey: { type: String, default: '' },
	// Scrolled to and highlighted once the list loads (the session card's Last session).
	focusSessionId: { type: String, default: '' },
})

const emit = defineEmits(['unavailable'])

const SESSIONS_LIMIT = 20

// null while the first load runs; false when core lacks the endpoints.
const available = ref(null)
// The worker has nothing for this stream yet (it never played).
const nothingRecorded = ref(false)
const loading = ref(false)
const loadError = ref('')
const sessions = ref([])
const info = ref(null)
const format = ref(DEFAULT_FORMAT)
const busy = ref('')
// sessionId -> { tone: 'muted' | 'error', text, retry? }; `retry` repeats a pending download.
const messages = ref({})

const clientNow = ref(Date.now())
let clock = null
let alive = true
const now = computed(() => clientNow.value + props.skewMs)

const formats = computed(() => info.value?.formats || ['vtt', 'srt', 'json'])
const maxSpanSec = computed(() => info.value?.maxSpanSec)

watch(formats, (list) => {
	if (!list.includes(format.value)) format.value = list.includes(DEFAULT_FORMAT) ? DEFAULT_FORMAT : list[0]
})

async function load({ quiet = false } = {}) {
	if (!quiet) loading.value = true
	loadError.value = ''
	try {
		const [list, exportInfo] = await Promise.all([
			CaptionService.getSessions(props.flowId, SESSIONS_LIMIT),
			CaptionService.getExportInfo(props.flowId).catch((err) => {
				if (err?.code === 'captions_export_not_found') return null
				throw err
			}),
		])
		if (!alive) return
		sessions.value = list
		info.value = exportInfo
		nothingRecorded.value = exportInfo === null
		available.value = true
		revealFocused()
	} catch (err) {
		if (!alive) return
		if (err?.status === 404) {
			available.value = false
			emit('unavailable')
			return
		}
		if (available.value === null) available.value = true
		loadError.value = err?.message || 'Could not load caption sessions.'
	} finally {
		loading.value = false
	}
}

const rows = computed(() => sessions.value.map((session) => {
	const running = isRunningSession(session, now.value)
	const { parts } = exportParts(session, { maxSpanSec: maxSpanSec.value, now: now.value })
	const langs = sessionExportLangs(session, info.value?.languages)
	return {
		session,
		running,
		parts,
		langs,
		startedLabel: formatSessionStart(session.startedAt),
		lengthLabel: formatDuration(sessionLengthSec(session, now.value)),
		langsLabel: joinLanguageNames(langs, props.catalog),
	}
}))

// sessionId -> DropDownMenu instance, to close the menu once a download starts.
const menus = new Map()
function setMenu(id, el) {
	if (el) menus.set(id, el)
	else menus.delete(id)
}

async function download(row, part, lang) {
	const key = `${row.session.id}:${part.index}:${lang}`
	if (busy.value) return
	menus.get(row.session.id)?.hide?.()
	busy.value = key
	messages.value = { ...messages.value, [row.session.id]: null }
	const ext = format.value
	try {
		const res = await CaptionService.downloadExport(props.flowId, { lang, ext, from: part.from, to: part.to, t0: part.t0 })
		if (res.segments === 0) {
			messages.value = { ...messages.value, [row.session.id]: { tone: 'muted', text: NO_CAPTIONS } }
			return
		}
		const filename = filenameFromDisposition(res.disposition) || fallbackFilename(info.value?.streamKey || props.streamKey, lang, part.from, ext)
		saveBlob(res.blob, filename)
		track(EVENTS.CAPTIONING_EXPORT_DOWNLOADED, { flowId: props.flowId, lang, format: ext, running: row.running })
	} catch (err) {
		const retry = isExportPending(err) ? () => download(row, part, lang) : null
		messages.value = { ...messages.value, [row.session.id]: { tone: retry ? 'muted' : 'error', text: exportErrorMessage(err), retry } }
	} finally {
		busy.value = ''
	}
}

const root = ref(null)
async function revealFocused() {
	if (!props.focusSessionId) return
	await nextTick()
	root.value?.querySelector(`[data-session="${CSS.escape(props.focusSessionId)}"]`)?.scrollIntoView({ block: 'nearest' })
}

watch(() => props.refreshKey, (next, prev) => {
	if (next !== prev && available.value) load({ quiet: true })
})

onMounted(() => {
	clock = setInterval(() => { clientNow.value = Date.now() }, 1000)
	load()
})
onBeforeUnmount(() => {
	alive = false
	clearInterval(clock)
})
</script>

<template>
	<div ref="root" class="flex flex-col gap-4" data-testid="caption-export">
		<p class="m-0 text-tiny text-surface-8">Save what was captioned in a recent session as a subtitle file. Timestamps start at the beginning of the session.</p>
		<div class="flex items-center justify-between gap-3">
			<span class="text-tiny font-medium text-surface-text-1">Format</span>
			<div class="flex items-center gap-2" role="group" aria-label="File format" data-testid="export-format">
				<button
					v-for="f in formats"
					:key="f"
					type="button"
					:aria-pressed="format === f ? 'true' : 'false'"
					class="inline-flex h-8 items-center rounded-full px-3 text-tiny font-medium ring-1 transition-colors"
					:class="format === f ? 'bg-main text-white ring-main' : 'bg-surface-3 text-surface-text-1 ring-white/5 hover:bg-surface-4 hover:text-white'"
					:data-format="f"
					@click="format = f"
				>
					{{ f.toUpperCase() }}
				</button>
			</div>
		</div>

		<p v-if="available === false" class="m-0 text-tiny text-surface-7" data-testid="export-unavailable">Caption downloads aren't available right now.</p>
		<p v-else-if="available === null || loading" class="m-0 text-tiny text-surface-7" data-testid="export-loading">Loading sessions…</p>
		<div v-else-if="loadError" class="flex flex-wrap items-center gap-2 text-tiny text-error-light" role="alert">
			<span>{{ loadError }}</span>
			<FormButton type="secondary" size="sm" @click="load()">Try again</FormButton>
		</div>
		<p v-else-if="nothingRecorded" class="m-0 text-tiny text-surface-7" data-testid="export-nothing-recorded">{{ NOTHING_RECORDED }}</p>
		<p v-else-if="!rows.length" class="m-0 text-tiny text-surface-7" data-testid="export-empty">
			No caption sessions yet. Each time you start captions, the session appears here.
		</p>
		<ul v-else class="m-0 flex list-none flex-col gap-2 p-0" data-testid="export-sessions">
			<li
				v-for="row in rows"
				:key="row.session.id"
				class="flex scroll-my-2 flex-col gap-2 rounded-lg px-3 py-2.5 ring-1 sm:flex-row sm:items-center sm:justify-between"
				:class="row.session.id === focusSessionId ? 'bg-surface-3/70 ring-main/60' : 'bg-surface-3/70 ring-transparent'"
				:data-session="row.session.id"
				:data-running="row.running ? 'true' : undefined"
				:data-highlighted="row.session.id === focusSessionId ? 'true' : undefined"
				data-testid="export-session"
			>
				<div class="flex min-w-0 flex-col gap-0.5">
					<div class="flex flex-wrap items-center gap-2">
						<span class="text-tiny font-medium text-surface-text-2">{{ row.startedLabel }}</span>
						<StatusPill v-if="row.running" variant="accent" pill pulse data-testid="session-running">Running</StatusPill>
					</div>
					<span class="text-xs text-surface-7">
						<span class="tabular-nums">{{ row.lengthLabel }}</span>
						<template v-if="row.langsLabel"> · {{ row.langsLabel }}</template>
						<template v-if="row.parts.length > 1"> · {{ row.parts.length }} parts</template>
					</span>
					<span v-if="!row.langs.length" class="text-xs text-surface-7" data-testid="export-no-langs">No captions available to download for this session.</span>
					<span v-if="row.running" class="text-xs text-gold" data-testid="snapshot-notice">{{ SNAPSHOT_NOTICE }}</span>
					<span
						v-if="messages[row.session.id]"
						class="text-xs"
						:class="messages[row.session.id].tone === 'error' ? 'text-error-light' : 'text-surface-8'"
						:role="messages[row.session.id].tone === 'error' ? 'alert' : 'status'"
						data-testid="export-message"
					>
						{{ messages[row.session.id].text }}
						<button
							v-if="messages[row.session.id].retry"
							type="button"
							class="ml-1 font-medium text-main-light hover:underline disabled:opacity-50"
							:disabled="!!busy"
							data-testid="export-retry"
							@click="messages[row.session.id].retry()"
						>Try again</button>
					</span>
				</div>
				<!-- Teleported so the scrolling modal body can't clip it; above the modal overlay. -->
				<DropDownMenu
					v-if="row.langs.length"
					:ref="(el) => setMenu(row.session.id, el)"
					type="secondary"
					size="sm"
					position="right"
					use-teleport
					custom-dropdown-class="!z-[60]"
					class="shrink-0 self-start sm:self-center"
					list-classes="min-w-[240px]"
					:aria-label="`Download captions from ${row.startedLabel}`"
					:disabled="!!busy"
				>
					<template #toggle-button>
						<span class="inline-flex items-center gap-1.5">
							{{ busy.startsWith(`${row.session.id}:`) ? 'Downloading…' : 'Download' }}
							<icon-base width="12" height="12" class="text-surface-7"><icon-chevron-down /></icon-base>
						</span>
					</template>
					<template #menu-items>
						<DropDownMenuItem v-if="row.running" is-text custom-classes="px-3 py-2 text-xs leading-snug text-gold whitespace-normal" :data-export-session="row.session.id" data-testid="export-snapshot-item">
							{{ SNAPSHOT_NOTICE }}
						</DropDownMenuItem>
						<template v-for="part in row.parts" :key="part.index">
							<DropDownMenuItem v-if="part.label" is-text custom-classes="px-3 pt-2 pb-1 text-[11px] font-semibold uppercase tracking-wide text-surface-7" :data-export-session="row.session.id" data-testid="export-part">
								{{ part.label }}
							</DropDownMenuItem>
							<DropDownMenuItem
								v-for="lang in row.langs"
								:key="`${part.index}-${lang}`"
								:data-export-session="row.session.id"
								:data-export-lang="lang"
								:data-part="part.index"
								@click="download(row, part, lang)"
							>
								{{ languageName(lang, catalog) }}
								<span class="ml-auto pl-3 font-mono text-[11px] uppercase text-surface-7">.{{ format }}</span>
							</DropDownMenuItem>
						</template>
					</template>
				</DropDownMenu>
			</li>
		</ul>
	</div>
</template>
